/**
 * SPEC-TrainingSimulator §8.3b — a lesson as a full-width page.
 *
 * Title, markdown body (the same SimpleMarkdown the Lessons tab previews
 * with), images with captions, one **Continue** button. No clock, no rail,
 * no footer. `/train/lesson/:id` walks the Phase 0 orientation lesson by
 * lesson (Continue → next unread → back to /train); the run page mounts the
 * same `LessonView` for start / before_item lessons inside an attempt.
 */

import { Link, useLocation, useParams } from "wouter";
import { Starling } from "@/lib/starling-brand";
import { SimpleMarkdown } from "@/components/simulator/simple-markdown";
import { useMarkLessonRead, useRunnerLesson, type RunnerLesson } from "@/lib/simulator-runner-queries";

const T = { bg: "#F7F8FA", card: "#FFFFFF", border: "#E5E7EB", text: "#111827", txt2: "#6B7280", purple: Starling.purple } as const;

export function LessonView({ lesson, onContinue, pending, exitHref }: { lesson: RunnerLesson; onContinue: () => void; pending?: boolean; exitHref?: string }) {
  return (
    <div data-testid="lesson-page" data-lesson-id={lesson.id} style={{ padding: "18px 22px 40px", fontFamily: Starling.fontBody, color: T.text, maxWidth: 820 }}>
      {exitHref && (
        <div style={{ marginBottom: 10 }}>
          <Link href={exitHref} data-testid="lesson-exit" style={{ fontSize: 12.5, color: T.txt2, textDecoration: "none" }}>← Back to Training</Link>
        </div>
      )}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "20px 24px" }}>
        <div style={{ fontFamily: Starling.fontHead, fontSize: 22, fontWeight: 600, marginBottom: 8 }} data-testid="lesson-title">{lesson.title}</div>
        <SimpleMarkdown source={lesson.bodyMd} testId="lesson-body" />
        {lesson.images.map((img, i) => (
          <figure key={i} style={{ margin: "14px 0 0" }}>
            <img src={img.src} alt={img.alt ?? ""} style={{ maxWidth: "100%", borderRadius: 6, border: `1px solid ${T.border}` }} />
            {img.caption && <figcaption style={{ fontSize: 12, color: T.txt2, marginTop: 4 }}>{img.caption}</figcaption>}
          </figure>
        ))}
        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            data-testid="lesson-continue"
            disabled={pending}
            onClick={onContinue}
            style={{ padding: "9px 22px", borderRadius: 8, fontSize: 13.5, fontWeight: 700, fontFamily: Starling.fontBody, border: `1px solid ${T.purple}`, background: T.purple, color: "#fff", cursor: pending ? "wait" : "pointer" }}
          >
            {pending ? "…" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TrainLesson() {
  const { id } = useParams<{ id: string }>();
  const q = useRunnerLesson(id);
  const mark = useMarkLessonRead();
  const [, navigate] = useLocation();
  if (q.isLoading) return <div style={{ padding: 24, fontFamily: Starling.fontBody }}>Loading lesson…</div>;
  if (q.isError || !q.data) return <div style={{ padding: 24, fontFamily: Starling.fontBody }}>Lesson not found. <Link href="/train">Back to Training</Link></div>;
  const onContinue = async () => {
    try {
      const r = await mark.mutateAsync(q.data.lesson.id);
      navigate(r.next ? `/train/lesson/${r.next}` : "/train");
    } catch {
      navigate("/train");
    }
  };
  return <LessonView lesson={q.data.lesson} onContinue={onContinue} pending={mark.isPending} exitHref="/train" />;
}
